// Letter generation, ported from letters.gs. Produces a print-ready A4 HTML
// document (one letter per order, page-break between) and opens it for the
// browser print dialog (print or save-as-PDF) — the browser equivalent of the
// legacy Google Doc. Past customers are skipped, exactly as the legacy did.

import { SOURCE, REPEAT_VERDICT } from '../constants';

const GOOGLE_REVIEW = 'https://g.page/r/CYyLzUkq5MkcEAE/review';
const CHANNEL_REVIEW = {
  [SOURCE.EBAY]: 'https://www.ebay.co.uk/myb/PurchaseHistory',
  [SOURCE.ETSY]: 'https://www.etsy.com/your/purchases',
};

const titleCase = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/\b\p{L}/gu, (m) => m.toUpperCase());

const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

const qr = (data) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(data)}`;

/** Should this order be skipped (past customer), mirroring the legacy skip. */
export function isPastCustomerOrder(order) {
  const r = order.repeatCustomer;
  if (!r) return false;
  if (r.verdict === REPEAT_VERDICT.YES) return true;
  if (r.verdict === REPEAT_VERDICT.MAYBE && r.confirmedByUser === true) return true;
  return false;
}

function addressBlock(order) {
  const s = order.shipping || {};
  const lines = [];
  const name = titleCase(s.name);
  if (name) lines.push(name);
  if (s.address1 && s.address1 !== name) lines.push(s.address1);
  if (s.address2) lines.push(s.address2);
  if (s.townCity) lines.push(s.townCity);
  if (s.county && s.county !== s.townCity) {
    lines.push(s.county.length > 3 ? s.county : s.county.toUpperCase());
  }
  if (s.postcode) lines.push(s.postcode.toUpperCase());
  if (s.country && s.country.toUpperCase() !== 'UNITED KINGDOM') lines.push(s.country.toUpperCase());
  return lines.map(esc).join('<br/>');
}

function letterHtml(order) {
  const isLeather = !!order.workflow?.isLeather;
  const ftoc = !!order.workflow?.ftoc;
  const firstName = titleCase(order.shipping?.name).split(' ')[0] || 'there';
  const source = order.source;

  const opener = ftoc
    ? 'Further to our conversation, thanks again for your order, I hope you are delighted with it!'
    : 'Firstly, thank you very much for your order, I hope you are delighted with it!';

  const leather = isLeather
    ? '<p>The leather on your sleeve is 100% natural, vegetable tanned leather. I would recommend giving it a gentle rub with a natural leather wax or cream once every few months or whenever you feel it is starting to stiffen or become dry.</p>'
    : '';

  const feedbackChannel =
    source === SOURCE.EBAY
      ? 'eBay or Google'
      : source === SOURCE.ETSY
      ? 'Etsy or Google'
      : 'the Emmerson Gray website or Google';

  const channelLink = CHANNEL_REVIEW[source];
  const qrs = `
    <div class="qr">
      ${channelLink ? `<figure><img src="${qr(channelLink)}" alt="review"/><figcaption>${source === SOURCE.EBAY ? 'eBay' : 'Etsy'} Review</figcaption></figure>` : ''}
      <figure><img src="${qr(GOOGLE_REVIEW)}" alt="google review"/><figcaption>Google Review</figcaption></figure>
    </div>`;

  return `
  <section class="letter">
    <div class="masthead">
      <h1>EMMERSON GRAY</h1>
      <p class="addr">3 Forstal Villas, Forstal Road, Egerton Forstal, Ashford, Kent TN27 9EN</p>
      <p class="contact">www.emmersongray.co.uk &nbsp;•&nbsp; sales@emmersongray.co.uk</p>
    </div>
    <div class="addressee">${addressBlock(order)}</div>
    <p>Dear ${esc(firstName)},</p>
    <p>${opener}</p>
    ${leather}
    <p>As a small business, I sink or swim based on the feedback and reviews I receive from my customers. I am genuinely passionate about delivering on my promises; so if you feel I haven’t met your expectations or you are not happy with your sleeve for any reason, please do not hesitate to get in touch with me so I can help resolve any issues.</p>
    <p>Assuming you’re happy with your new sleeve, I would be very grateful if you’d be kind enough to take a few minutes to leave feedback on ${feedbackChannel}.</p>
    ${qrs}
    <p>Thank you again for your purchase!</p>
    <p>Yours sincerely</p>
    <p class="signoff">Kate (Owner)<br/>Emmerson Gray</p>
    <p class="ps">PS. I make sleeves for most devices including phones, tablets, and laptops, and am happy to make bespoke sizes if you have something unusual in mind, or if your device has an additional keyboard or cover etc! Please don’t hesitate to email me at kate@emmersongray.co.uk if you are considering another sleeve and I’d be delighted to help!</p>
  </section>`;
}

const DOC_CSS = `
  @page { size: A4; margin: 18mm; }
  body { font-family: Cambria, Georgia, serif; font-size: 11pt; color: #000; }
  .letter { page-break-after: always; }
  .letter:last-child { page-break-after: auto; }
  .masthead { text-align: center; margin-bottom: 8mm; }
  .masthead h1 { font-family: 'Voltaire', Arial, sans-serif; font-size: 40pt; margin: 0; font-weight: normal; letter-spacing: 1px; }
  .masthead .addr { font-size: 10pt; margin: 2mm 0 0; }
  .masthead .contact { font-size: 9pt; margin: 1mm 0 0; }
  .addressee { margin: 6mm 0; line-height: 1.4; }
  p { line-height: 1.45; text-align: justify; }
  .qr { display: flex; gap: 18mm; justify-content: center; margin: 6mm 0; }
  .qr figure { margin: 0; text-align: center; font-size: 9pt; }
  .signoff { margin-top: 8mm; }
  .ps { font-size: 9pt; margin-top: 6mm; }
`;

/**
 * Open a print-ready letters document for the given orders (skipping past
 * customers). Returns the number of letters generated.
 */
export function openLetters(orders) {
  const eligible = orders.filter((o) => !isPastCustomerOrder(o));
  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Pop-up blocked — allow pop-ups to generate letters.');
  }
  const body = eligible.length
    ? eligible.map(letterHtml).join('\n')
    : '<p style="font-family:sans-serif">No letters generated (all selected orders are past customers).</p>';
  win.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>EG Letters</title><style>${DOC_CSS}</style></head><body>${body}</body></html>`
  );
  win.document.close();
  win.focus();
  if (eligible.length) setTimeout(() => win.print(), 500); // give QR images a moment
  return eligible.length;
}
